/**
 * Per-editor chrome: find bar, word-count status, autosave, restore, leave warning.
 *
 * A ProseMirror plugin `view` rather than a toolbar custom control, because the
 * find bar and the status line are not toolbar buttons. Putting them in the bar
 * would add tab stops to a control that is deliberately one stop.
 */

import { serializeHtml } from '@openleaf-editor/core'
import { announce, fill, liveRegion, t, withLocale } from '@openleaf-editor/ui'
import type { Node as PMNode } from 'prosemirror-model'
import { Plugin, type EditorState } from 'prosemirror-state'
import type { EditorView } from 'prosemirror-view'
import {
  editorHost,
  newDocument,
  previewDocument,
  printDocument,
  saveDocument,
  showWordCount,
  type EditorHost,
} from './actions.js'
import { documentStats, formatWordCount } from './count.js'
import { confirmAction } from './dialogs.js'
import {
  clearDraft,
  defaultStorage,
  draftStorageKey,
  purgeDrafts,
  readDraft,
  writeDraft,
  type DraftStorage,
} from './draft.js'
import { observeDraftStorage } from './draft-errors.js'
import {
  clearSearch,
  findNext,
  findPrev,
  replaceAll,
  replaceCurrent,
  searchKey,
  setSearch,
} from './search.js'

export interface SessionOptions {
  /** Persist drafts to storage. Default true. */
  autosave?: boolean
  /** Milliseconds to wait after a change before writing a draft. Default 800. */
  debounceMs?: number
  /** `beforeunload` when the document differs from the last save. Default true. */
  warnBeforeLeave?: boolean
  /** Offer to restore a stored draft that differs from the loaded HTML. Default true. */
  restore?: boolean
  /** Storage backend. Tests inject a fake; production uses localStorage. */
  storage?: DraftStorage
}

interface SessionHandle {
  host: EditorHost
  openFind: () => void
  closeFind: () => void
  markClean: () => void
  isDirty: () => boolean
  /**
   * `prevState` is the state this update replaced, as ProseMirror hands it to a
   * plugin view. Omitted for the calls that are not view updates -- the first
   * paint, and a restored draft -- which is what tells them apart from a caret
   * move and lets them render the count immediately instead of debouncing it.
   */
  update: (prevState?: EditorState) => void
  destroy: () => void
}

/**
 * Emitted by `<openleaf-editor>` when the HTML source box opens and closes.
 *
 * Named here rather than imported: this plugin does not depend on the element
 * package, and the element declares the same names for the same reason.
 */
const SOURCE_OPEN_EVENT = 'openleaf:source-open'
const SOURCE_CLOSE_EVENT = 'openleaf:source-close'

/** Milliseconds a burst of typing coalesces into one recount. */
const COUNT_DEBOUNCE_MS = 250

interface Baseline {
  /** The HTML as of the last save. */
  html: string
  /**
   * The document as of the last save.
   *
   * The dirty check used to be a full serialization of the whole document, run
   * on every autosave tick and again inside `beforeunload` for every editor on
   * the page -- a hundred-page document cost about forty milliseconds to decide
   * it had not changed. Comparing nodes answers the common case (nothing has
   * changed) without building a string, and `Node.eq` short-circuits on
   * identity, which is what a document nobody has typed into actually is.
   *
   * Null only until the first baseline is taken.
   */
  doc: PMNode | null
  /**
   * Set when the author edits HTML in the source box.
   *
   * Those edits land in a textarea and dispatch no transaction, so the document
   * comparison cannot see them and would report a clean session. The flag sends
   * the check back to the string comparison, and clears itself the moment that
   * comparison proves the session clean again.
   */
  sourceEdited: boolean
  /** Whether a stored draft has already been offered for this host. */
  offeredRestore: boolean
}

const handles = new WeakMap<HTMLElement, SessionHandle>()
/**
 * Held per host rather than in the plugin view's closure.
 *
 * Registering another opt-in plugin reconfigures the editor state, and
 * ProseMirror destroys and recreates every plugin view when it does. A baseline
 * that lived in the closure would be re-read from the current document on the
 * way back up, adopting the author's unsaved edits as though they had been
 * saved -- and the next update would then clear the recovery draft the departing
 * view had just written, taking the leave warning with it.
 */
const baselines = new WeakMap<HTMLElement, Baseline>()
const live = new Set<SessionHandle>()
const guardedWindows = new WeakSet<Window>()

function baselineFor(host: EditorHost, html: () => string, doc: PMNode): Baseline {
  const existing = baselines.get(host)
  if (existing) return existing
  const created: Baseline = { html: html(), doc, sourceEdited: false, offeredRestore: false }
  baselines.set(host, created)
  return created
}

export function sessionFor(host: HTMLElement): SessionHandle | undefined {
  return handles.get(host)
}

function ensureLeaveGuard(win: Window): void {
  if (guardedWindows.has(win)) return
  guardedWindows.add(win)
  win.addEventListener('beforeunload', (event) => {
    for (const session of live) {
      if (session.isDirty()) {
        event.preventDefault()
        event.returnValue = ''
        return
      }
    }
  })
}

function button(doc: Document, label: string): HTMLButtonElement {
  const el = doc.createElement('button')
  el.type = 'button'
  el.textContent = label
  return el
}

/**
 * A UI string in this editor's own language, with `{name}` placeholders filled.
 *
 * A template rather than concatenation, because "2 of 7" is not two words in
 * every language and neither is the order they come in.
 */
function say(host: HTMLElement, source: string, values: Record<string, string> = {}): string {
  return withLocale(host.getAttribute('lang'), () =>
    fill(t(source), values),
  )
}

/** How much of the surrounding sentence to read with a match. */
const MATCH_CONTEXT = 40

export function sessionChrome(options: SessionOptions = {}): Plugin {
  const autosave = options.autosave !== false
  const warn = options.warnBeforeLeave !== false
  const restore = options.restore !== false
  const debounceMs = options.debounceMs ?? 800
  const storage = options.storage ?? defaultStorage()

  return new Plugin({
    view(view) {
      const host = editorHost(view.dom)
      if (!host) return {}
      const handle = attachSession(host, view, {
        autosave,
        warn,
        restore,
        debounceMs,
        storage,
      })
      return {
        update: (_view, prevState) => handle.update(prevState),
        destroy: () => handle.destroy(),
      }
    },
  })
}

function attachSession(
  host: EditorHost,
  view: EditorView,
  options: {
    autosave: boolean
    warn: boolean
    restore: boolean
    debounceMs: number
    storage: DraftStorage
  },
): SessionHandle {
  const doc = host.ownerDocument
  const win = doc.defaultView
  const key = draftStorageKey(host)
  let timer: ReturnType<typeof setTimeout> | undefined
  let countTimer: ReturnType<typeof setTimeout> | undefined

  // Only the key for this page is ever read back, so a draft for a page nobody
  // returns to would sit in storage for good. Attaching an editor is the one
  // moment this library reliably gets, and the sweep is a walk of the keys.
  purgeDrafts(options.storage)
  const storage = observeDraftStorage(host, options.storage)

  /**
   * The HTML the editor is showing right now.
   *
   * `host.value` is the authority once the element has wired up its view: with
   * the HTML source box open the live document is the textarea's text, and
   * comparing the untouched `view.state.doc` would report a session that has
   * been edited in source mode as clean. During this plugin view's construction
   * the element has not assigned its `#view` yet, so its getter would hand back
   * the raw textarea string -- serialize the document instead.
   */
  const currentHtml = (): string => (host.view ? host.value : serializeHtml(view.state.doc))

  const baseline = baselineFor(host, currentHtml, view.state.doc)

  const findBar = buildFindBar(host, view)
  const status = doc.createElement('div')
  status.className = 'ol-status'
  // Visual only. A live region here would announce on every keystroke.
  status.setAttribute('aria-hidden', 'true')
  const toolbar = host.querySelector('[role="toolbar"]')
  if (toolbar) toolbar.after(findBar.root)
  else host.prepend(findBar.root)
  host.appendChild(status)

  /**
   * True when the document is provably the saved one, without building a string.
   *
   * Only the "clean" answer is taken from the node comparison. A document that
   * differs structurally still falls through to the string comparison, because
   * `host.value` -- not `view.state.doc` -- is the authority while the source
   * box is open, and because two different node trees can still serialize to
   * the same HTML. Being slower to say "dirty" costs a serialization the author
   * has already earned by typing; being wrong about "clean" costs their work.
   */
  const cleanByDoc = (): boolean =>
    !baseline.sourceEdited && baseline.doc !== null && baseline.doc.eq(view.state.doc)

  const isDirty = (): boolean => {
    if (cleanByDoc()) return false
    const dirty = currentHtml() !== baseline.html
    if (!dirty) baseline.sourceEdited = false
    return dirty
  }

  const persist = (): void => {
    if (!options.autosave) return
    if (cleanByDoc()) {
      clearDraft(storage, key)
      return
    }
    const html = currentHtml()
    if (html === baseline.html) {
      baseline.sourceEdited = false
      clearDraft(storage, key)
      return
    }
    writeDraft(storage, key, html)
  }

  const schedule = (): void => {
    if (!options.autosave) return
    if (timer !== undefined) clearTimeout(timer)
    timer = setTimeout(persist, options.debounceMs)
  }

  const markSaved = (): void => {
    baseline.html = currentHtml()
    baseline.doc = view.state.doc
    baseline.sourceEdited = false
  }

  // Submission can be canceled, fail on the network, or be rejected by the
  // server. Flush recovery now; only an acknowledged save may clear it.
  const onSubmit = (): void => persist()

  const form = boundForm(host)
  form?.addEventListener('submit', onSubmit)

  // While the source box is open the author's edits land in a textarea, which
  // dispatches no ProseMirror transactions -- so this plugin view's `update`
  // never runs and the debounce is never rearmed. Watching the textarea is what
  // keeps source-mode edits autosaved and keeps them counted as unsaved on the
  // way out of the tab.
  let sourceArea: HTMLTextAreaElement | null = null
  const onSourceInput = (): void => {
    // The document does not move while the source box is open, so the node
    // comparison would call this clean. Say so explicitly.
    baseline.sourceEdited = true
    schedule()
  }
  const onSourceOpen = (event: Event): void => {
    const area = (event as CustomEvent<{ textarea?: HTMLTextAreaElement }>).detail?.textarea
    if (!area) return
    sourceArea?.removeEventListener('input', onSourceInput)
    sourceArea = area
    area.addEventListener('input', onSourceInput)
  }
  const onSourceClose = (): void => {
    sourceArea?.removeEventListener('input', onSourceInput)
    sourceArea = null
    // Leaving source mode need not change the document -- the element compares
    // documents rather than strings, so merely looking at the source dispatches
    // nothing -- which leaves no update to settle the draft. Settle it here.
    schedule()
  }
  host.addEventListener(SOURCE_OPEN_EVENT, onSourceOpen)
  host.addEventListener(SOURCE_CLOSE_EVENT, onSourceClose)

  const renderCount = (): void => {
    countTimer = undefined
    status.textContent = withLocale(host.getAttribute('lang'), () =>
      formatWordCount(documentStats(view.state.doc), host.getAttribute('lang')),
    )
  }

  /**
   * Recount after the typing stops.
   *
   * The status line is decoration -- `aria-hidden`, no live region, nobody is
   * waiting on it -- and counting words is a walk of the whole document. Doing
   * that between two keystrokes spends a third of a frame on a number that is
   * about to be wrong again.
   */
  const scheduleCount = (): void => {
    if (countTimer !== undefined) clearTimeout(countTimer)
    countTimer = setTimeout(renderCount, COUNT_DEBOUNCE_MS)
  }

  const handle: SessionHandle = {
    host,
    openFind: () => findBar.open(),
    closeFind: () => findBar.close(),
    markClean: () => {
      markSaved()
      clearDraft(storage, key)
    },
    isDirty,
    update: (prevState) => {
      // A caret move, a click, a drag-selection and a plugin-state change all
      // arrive here as updates. Only a changed document can change the count or
      // the draft, and comparing documents is an identity check in the case
      // that matters, because a selection-only transaction reuses the node.
      const changed = prevState === undefined || !prevState.doc.eq(view.state.doc)
      findBar.sync()
      if (!changed) return
      // The first paint and a restored draft have no previous state and no
      // typing to coalesce with, so they render straight away rather than
      // leaving the status line blank for a quarter of a second.
      if (prevState === undefined) renderCount()
      else scheduleCount()
      if (options.autosave) schedule()
    },
    destroy: () => {
      if (countTimer !== undefined) clearTimeout(countTimer)
      if (timer !== undefined) clearTimeout(timer)
      persist()
      form?.removeEventListener('submit', onSubmit)
      sourceArea?.removeEventListener('input', onSourceInput)
      host.removeEventListener(SOURCE_OPEN_EVENT, onSourceOpen)
      host.removeEventListener(SOURCE_CLOSE_EVENT, onSourceClose)
      findBar.root.remove()
      status.remove()
      live.delete(handle)
      handles.delete(host)
    },
  }

  handles.set(host, handle)
  live.add(handle)
  handle.update()

  if (options.warn && win) ensureLeaveGuard(win)

  if (options.restore && !baseline.offeredRestore) {
    const draft = readDraft(storage, key)
    // Not offered when the draft is what is already on screen. A plugin view
    // restart writes a draft of the unsaved document as it goes, and offering to
    // restore the document the author is looking at is noise. Asked at most once
    // per host, so declining survives a reconfiguration too.
    if (draft && draft.html !== baseline.html && draft.html !== currentHtml()) {
      baseline.offeredRestore = true
      queueMicrotask(() => {
        void offerRestore(host, draft.html, draft.savedAt, handle)
      })
    }
  }

  return handle
}

function boundForm(host: EditorHost): HTMLFormElement | null {
  const id = host.getAttribute('for')
  if (id) {
    const textarea = host.ownerDocument.getElementById(id)
    if (textarea instanceof HTMLTextAreaElement) return textarea.form
  }
  return host.closest('form')
}

async function offerRestore(
  host: EditorHost,
  html: string,
  savedAt: number,
  handle: SessionHandle,
): Promise<void> {
  const when = new Date(savedAt).toLocaleString()
  const ok = await confirmAction(host.ownerDocument, {
    title: 'Restore unsaved draft',
    message: `A draft from ${when} is saved in this browser. Restore it?`,
    confirmLabel: 'Restore draft',
  }, host)
  if (!ok) return
  host.value = html
  handle.update()
}

function buildFindBar(host: EditorHost, view: EditorView): { root: HTMLElement; open: () => void; close: () => void; sync: () => void } {
  const doc = host.ownerDocument
  const text = (source: string, values?: Record<string, string>): string =>
    say(host, source, values)

  const root = doc.createElement('div')
  root.className = 'ol-find'
  root.hidden = true
  root.setAttribute('role', 'search')
  root.setAttribute('aria-label', text('Find and replace'))

  // Mounted now, and on the HOST rather than in the bar. The count used to be a
  // live region INSIDE this subtree, which carries `hidden` until the moment it
  // has something to say -- so `open()` unhid the subtree and populated it in
  // one task and the first result count was very likely swallowed. A region a
  // reader has never seen is not a quiet region, it is a silent one.
  liveRegion(host)

  const findLabel = doc.createElement('label')
  const findText = doc.createElement('span')
  findText.textContent = text('Find')
  const findInput = doc.createElement('input')
  findInput.type = 'search'
  findInput.name = 'find'
  findLabel.append(findText, findInput)

  const replaceLabel = doc.createElement('label')
  const replaceText = doc.createElement('span')
  replaceText.textContent = text('Replace')
  const replaceInput = doc.createElement('input')
  replaceInput.type = 'text'
  replaceInput.name = 'replace'
  replaceLabel.append(replaceText, replaceInput)

  const caseLabel = doc.createElement('label')
  caseLabel.className = 'ol-check'
  const caseBox = doc.createElement('input')
  caseBox.type = 'checkbox'
  caseBox.name = 'matchCase'
  const caseSpan = doc.createElement('span')
  caseSpan.textContent = text('Match case')
  caseLabel.append(caseBox, caseSpan)

  // Visual only now. What is spoken goes to the host's region, which is not
  // inside a subtree that is hidden for most of its life.
  const count = doc.createElement('span')
  count.className = 'ol-find-count'
  count.setAttribute('aria-hidden', 'true')

  const prev = button(doc, text('Previous'))
  const next = button(doc, text('Next'))
  const replace = button(doc, text('Replace'))
  const replaceAllBtn = button(doc, text('Replace all'))
  const closeBtn = button(doc, text('Close'))

  root.append(findLabel, replaceLabel, caseLabel, count, prev, next, replace, replaceAllBtn, closeBtn)

  const applyQuery = (): void => {
    setSearch(findInput.value, caseBox.checked)(view.state, view.dispatch)
  }

  /**
   * The find field, focused and selected.
   *
   * Shared by `open()` and Replace all so the two cannot drift. After Replace
   * all the button disables (`hits === 0`) and a disabled focused button dumps
   * focus to `<body>` in every engine -- so the author who just rewrote the
   * document is left on the page, not in the query they still have. The replace
   * field is the other candidate; the find field is the one `open()` already
   * uses, and with no matches left the next thing the author does is edit the
   * query or close, not type a replacement.
   */
  const focusFind = (): void => {
    findInput.focus()
    findInput.select()
  }

  const open = (): void => {
    root.hidden = false
    const selected = view.state.doc.textBetween(view.state.selection.from, view.state.selection.to, ' ')
    if (selected && selected !== findInput.value) findInput.value = selected
    applyQuery()
    focusFind()
  }

  const close = (): void => {
    root.hidden = true
    clearSearch(view.state, view.dispatch)
    view.focus()
  }

  /**
   * The text around a match, so the announcement carries the match itself.
   *
   * Jumping to a result moved the SELECTION but not DOM focus -- focus stays in
   * the input, so a screen reader's reading cursor never goes near the
   * highlight. The author heard "2 of 7" and was then asked to press Replace on
   * text they had no way to read.
   */
  const contextAround = (from: number, to: number): string => {
    const { doc: pmDoc } = view.state
    const start = Math.max(0, from - MATCH_CONTEXT)
    const end = Math.min(pmDoc.content.size, to + MATCH_CONTEXT)
    return pmDoc.textBetween(start, end, ' ', ' ').trim()
  }

  /** The last thing said, so an unrelated keystroke does not repeat it. */
  let spoken = ''

  const sync = (): void => {
    const search = searchKey.getState(view.state)
    const hits = search ? search.matches.length : 0
    // A button that does nothing when pressed is worse than one that says it
    // cannot: Replace was reachable with no current match and silently returned.
    prev.disabled = hits === 0
    next.disabled = hits === 0
    replace.disabled = hits === 0
    replaceAllBtn.disabled = hits === 0

    if (!search || search.query.length === 0) {
      count.textContent = ''
      spoken = ''
      return
    }

    // `replaced` is reported before the match count, because replacing rebuilds
    // the matches against the new document and finds none of the old ones --
    // reporting that as "No matches" made a successful Replace all read as a
    // failure.
    let message: string
    if (search.replaced > 0) {
      message = say(host, '{n} replaced', { n: String(search.replaced) })
      count.textContent = message
    } else if (search.matches.length === 0) {
      message = say(host, 'No matches')
      count.textContent = message
    } else {
      const total = String(search.matches.length)
      const current = search.index >= 0 ? search.index + 1 : 0
      const match = search.index >= 0 ? search.matches[search.index] : undefined
      if (current > 0 && match) {
        count.textContent = say(host, '{current} of {total}', { current: String(current), total })
        message = say(host, '{current} of {total}: {context}', {
          current: String(current),
          total,
          context: contextAround(match.from, match.to),
        })
      } else {
        message = say(host, '{total} matches', { total })
        count.textContent = message
      }
    }

    // Guarded on the text, not on an event: `sync` runs on every transaction,
    // and repeating an identical count over the author's typing is the reason
    // announcements get switched off.
    if (message !== spoken) {
      spoken = message
      announce(host, message)
    }
  }

  findInput.addEventListener('input', applyQuery)
  caseBox.addEventListener('change', applyQuery)
  // Escape delegated on the bar, not bound to two of its eight controls. Bound
  // per input, the author who had tabbed to Next, Replace or the case checkbox
  // pressed Escape and nothing happened.
  root.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return
    event.preventDefault()
    close()
  })
  findInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (event.shiftKey) findPrev(view.state, view.dispatch)
      else findNext(view.state, view.dispatch)
    }
  })
  replaceInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      replaceCurrent(replaceInput.value)(view.state, view.dispatch)
    }
  })
  prev.addEventListener('click', () => findPrev(view.state, view.dispatch))
  next.addEventListener('click', () => findNext(view.state, view.dispatch))
  replace.addEventListener('click', () => replaceCurrent(replaceInput.value)(view.state, view.dispatch))
  replaceAllBtn.addEventListener('click', () => {
    // Focus before the replace, and only while the bar is open. `replaceAll`
    // dispatches; `sync` disables this button (`hits === 0`) and announces
    // "{n} replaced". A disabled focused button dumps focus to `<body>` in
    // every engine, and a focus move *after* the announcement can interrupt
    // a screen reader mid-utterance -- `announce` queues the live region on
    // a 60 ms timer, but focusing first is the order that stays correct if
    // that delay ever goes. Do not call `open()`: it would copy the current
    // selection into the query.
    if (!root.hidden) focusFind()
    replaceAll(replaceInput.value)(view.state, view.dispatch)
  })
  closeBtn.addEventListener('click', close)

  return { root, open, close, sync }
}

export function runFind(view: EditorView): boolean {
  const host = editorHost(view.dom)
  if (!host) return false
  sessionFor(host)?.openFind()
  return true
}

export function runSave(view: EditorView): boolean {
  const host = editorHost(view.dom)
  if (!host) return false
  const submittedHtml = host.value
  void saveDocument(host).then((saved) => {
    // The author can keep typing during the request. A successful save of an
    // older snapshot must not mark those newer edits clean or erase recovery.
    if (saved && host.value === submittedHtml) sessionFor(host)?.markClean()
  }).catch(() => {
    announce(host, withLocale(host.getAttribute('lang'), () => t('Could not save. Your changes are still here.')))
    host.dispatchEvent(new CustomEvent('openleaf:save-error', { bubbles: true, composed: true }))
  })
  return true
}

export function runPreview(view: EditorView): boolean {
  const host = editorHost(view.dom)
  if (!host) return false
  previewDocument(host)
  return true
}

export function runPrint(view: EditorView): boolean {
  const host = editorHost(view.dom)
  if (!host) return false
  printDocument(host)
  return true
}

export function runWordCount(view: EditorView): boolean {
  const host = editorHost(view.dom)
  if (!host) return false
  showWordCount(host)
  return true
}

export function runNewDocument(view: EditorView): boolean {
  const host = editorHost(view.dom)
  if (!host) return false
  const handle = sessionFor(host)
  void newDocument(host, () => handle?.isDirty() === true).then((cleared) => {
    if (cleared) handle?.markClean()
  })
  return true
}
